import { TRANSLATION_MEMORY_ENTRIES_QUERY } from '@src/gql/translation-memory-gql';
import { extractErrorInfo } from '@src/utility/error';
import { chunkArray } from '@src/utility/general';
import { LogManager } from '@src/utility/Logger';

import type ApiClient from '../apiClient/ApiClient';
import type { Client } from '@datocms/cma-client-browser';
import type { GraphQLClient } from 'graphql-request';

/** A reference to a shared `language` record (source / translation language). */
export interface LanguageRef {
  id: string;
  caption?: string | null;
  value?: string | null;
}

/** A person ref (creator / last editor). */
export interface EntryPersonRef {
  id: string;
  name?: string | null;
}

/**
 * One translation-memory entry (PLAT-10838 `translation_memory_entry`) — a
 * directional term rule. `collectionId` empty = Default/uncategorized (E12);
 * an empty `translationLanguage` ⇒ all target languages, and
 * `translation === sourceTerm` ⇒ do-not-translate (E4). `createdAt`/`updatedAt`
 * come from Dato record meta.
 */
export interface TranslationMemoryEntry {
  id: string;
  sourceTerm: string;
  translation: string;
  comment?: string | null;
  origin: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  collectionId: string | null;
  sourceLanguage: LanguageRef | null;
  translationLanguage: LanguageRef | null;
  createdByUser?: EntryPersonRef | null;
  lastUpdatedByUser?: EntryPersonRef | null;
}

/** How an entry was added (E12). Extensible enum on the model. */
export type EntryOrigin = 'manual' | 'import' | 'promoted';

/**
 * Provenance for a non-manual write: the `origin` to stamp and, for imports, the
 * originating `translation_memory_import` id (E11/E12). Omitted ⇒ `manual` / no
 * import link (the manual-CRUD default, PLAT-10845).
 */
export interface EntryProvenance {
  origin?: EntryOrigin;
  importId?: string | null;
}

/** Editable fields on an entry (language pickers pass full refs for display). */
export interface TranslationMemoryEntryInput {
  sourceTerm: string;
  sourceLanguage: LanguageRef;
  translation: string;
  /** `null` ⇒ applies to all target languages (E4). */
  translationLanguage: LanguageRef | null;
  comment?: string | null;
  /** `null` ⇒ Default (uncategorized) bucket (E12). */
  collectionId: string | null;
}

/**
 * One entry in a bulk write (`writeMany`). `existingEntryId` present ⇒ overwrite
 * that record; absent ⇒ create a new one.
 */
export interface EntryBulkWrite {
  input: TranslationMemoryEntryInput;
  existingEntryId?: string | null;
}

/** Confirmed entries from a bulk write, split by create vs overwrite. */
export interface BulkWriteResult {
  created: TranslationMemoryEntry[];
  overwritten: TranslationMemoryEntry[];
  /**
   * Writes that threw and were dropped (PLAT-10871). Individual failures are
   * logged and skipped rather than aborting the batch, so without this count a
   * partly-failed commit reports as a clean success.
   */
  failed: number;
}

/**
 * A showcase's entries plus whether the paged read hit its page ceiling
 * (PLAT-10871). `truncated` ⇒ entries exist beyond what was loaded, so the list
 * is incomplete and callers must say so rather than present it as the whole set.
 */
export interface ShowcaseEntries {
  entries: TranslationMemoryEntry[];
  truncated: boolean;
}

/** Raw GraphQL shape (nested links) before flattening to `TranslationMemoryEntry`. */
interface RawEntry {
  id: string;
  sourceTerm: string;
  translation: string;
  comment: string | null;
  origin: string;
  _createdAt: string | null;
  _updatedAt: string | null;
  collection: { id: string } | null;
  sourceLanguage: LanguageRef | null;
  translationLanguage: LanguageRef | null;
  createdByUser: EntryPersonRef | null;
  lastUpdatedByUser: EntryPersonRef | null;
}

interface EntriesResponse {
  allTranslationMemoryEntries: RawEntry[];
}

const mapEntry = (raw: RawEntry): TranslationMemoryEntry => ({
  id: raw.id,
  sourceTerm: raw.sourceTerm,
  translation: raw.translation,
  comment: raw.comment,
  origin: raw.origin,
  createdAt: raw._createdAt,
  updatedAt: raw._updatedAt,
  collectionId: raw.collection?.id ?? null,
  sourceLanguage: raw.sourceLanguage,
  translationLanguage: raw.translationLanguage,
  createdByUser: raw.createdByUser,
  lastUpdatedByUser: raw.lastUpdatedByUser,
});

/**
 * CRUD over `translation_memory_entry` — GraphQL read (filtered by showcase),
 * CMA writes. Mirrors `TranslationMemoryCollectionRepository`: mutations return
 * null/false on failure (logged with `extractErrorInfo`) and publish after each
 * write; `fetchForShowcase` **rethrows** so a failed load surfaces as an error
 * state, not a false "no entries". Hard `items.destroy` (no `is_deleted` field).
 */
export class TranslationMemoryEntryRepository {
  /** Cap on concurrent CMA writes per batch (avoids thrashing the rate limit). */
  private static readonly WRITE_CONCURRENCY = 20;
  /** Max ids per `items.bulkPublish` call. */
  private static readonly PUBLISH_BATCH = 200;
  /** DatoCMS `first` ceiling — page size for the paged read of entries. */
  private static readonly PAGE_SIZE = 100;
  /** Guard against a runaway paging loop (DatoCMS caps a filtered set well under this). */
  private static readonly MAX_PAGES = 50;

  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;
  private logger = LogManager.getLogger(
    'repository:TranslationMemoryEntryRepository',
  );
  /** Cached `translation_memory_entry` item-type id (looked up once per repo). */
  private itemTypeIdPromise?: Promise<string>;

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
  }

  /**
   * Resolve (and cache) the `translation_memory_entry` item-type id. The id is
   * immutable, so one lookup per repo instance suffices; a failed lookup is not
   * cached so the next write retries.
   */
  private entryItemTypeId(): Promise<string> {
    if (!this.itemTypeIdPromise) {
      this.itemTypeIdPromise = this.dClient.itemTypes
        .find('translation_memory_entry')
        .then(itemType => itemType.id)
        .catch(error => {
          this.itemTypeIdPromise = undefined;
          throw error;
        });
    }
    return this.itemTypeIdPromise;
  }

  /**
   * All entries for a showcase, ordered by source term. Rethrows on failure.
   *
   * Paged: DatoCMS caps `first` at 100, so we loop with `skip` until a short
   * page (mirrors the backend TM loader). Loading only the first page dropped
   * every entry past it from the client, breaking per-fragment TM provenance
   * resolution in Translation Review (PLAT-10850).
   *
   * The loop can exit two ways, and they are not the same outcome: a short page
   * means the whole set was read, while exhausting `MAX_PAGES` means there is
   * more we did not fetch. Only the first is complete, so the second is reported
   * as `truncated` (PLAT-10871) — raising the ceiling would only move the cliff,
   * and a silently short list reads as "these are all the entries".
   *
   * A full page at the ceiling is NOT by itself proof of truncation: a workspace
   * holding exactly `MAX_PAGES × PAGE_SIZE` entries is complete, and that
   * boundary is reachable in practice (a single import can hold exactly
   * `IMPORT_ROW_CAP` = 5,000 rows). So the ceiling is confirmed with one probe
   * page past it — an extra round trip, but only for a workspace already large
   * enough to have cost 50 of them, and it is what keeps the warning honest.
   */
  async fetchForShowcase(showcaseId: string): Promise<ShowcaseEntries> {
    try {
      const all: TranslationMemoryEntry[] = [];
      let truncated = false;
      for (
        let page = 0;
        page <= TranslationMemoryEntryRepository.MAX_PAGES;
        page++
      ) {
        const response = await this.gqlClient.request<EntriesResponse>(
          TRANSLATION_MEMORY_ENTRIES_QUERY,
          {
            showcaseId,
            skip: page * TranslationMemoryEntryRepository.PAGE_SIZE,
          },
        );
        const rows = response.allTranslationMemoryEntries ?? [];
        if (page === TranslationMemoryEntryRepository.MAX_PAGES) {
          // The probe: anything here is an entry we are NOT returning.
          truncated = rows.length > 0;
          break;
        }
        for (const row of rows) all.push(mapEntry(row));
        if (rows.length < TranslationMemoryEntryRepository.PAGE_SIZE) break;
      }
      if (truncated) {
        this.logger.warn('Translation memory entry read hit its page ceiling', {
          showcaseId,
          loaded: all.length,
        });
      }
      return { entries: all, truncated };
    } catch (error) {
      this.logger.error(
        'Error fetching translation memory entries',
        extractErrorInfo(error),
      );
      throw error;
    }
  }

  /**
   * Create an entry under a showcase, stamping the creator person ref. Defaults
   * to `origin=manual` with no import link (manual CRUD, PLAT-10845); imports
   * pass `{ origin: 'import', importId }` to record provenance (E11/E12).
   */
  async create(
    showcaseId: string,
    input: TranslationMemoryEntryInput,
    personId: string | null,
    provenance: EntryProvenance = {},
  ): Promise<TranslationMemoryEntry | null> {
    try {
      const itemTypeId = await this.entryItemTypeId();
      const origin = provenance.origin ?? 'manual';
      const created = await this.dClient.items.create({
        item_type: { type: 'item_type', id: itemTypeId },
        showcase: showcaseId,
        collection: input.collectionId,
        source_term: input.sourceTerm,
        source_language: input.sourceLanguage.id,
        translation: input.translation,
        translation_language: input.translationLanguage?.id ?? null,
        comment: input.comment ?? '',
        origin,
        import: provenance.importId ?? null,
        created_by_user: personId,
        last_updated_by_user: personId,
      });
      await this.dClient.items.publish(created.id);
      return this.echo(created.id, input, origin);
    } catch (error) {
      this.logger.error(
        'Error creating translation memory entry',
        extractErrorInfo(error),
      );
      return null;
    }
  }

  /**
   * Update an entry's fields (incl. its collection = move), stamping the editor.
   * Manual edits leave `origin`/`import` untouched; an import overwrite passes
   * `{ origin: 'import', importId }` so the overwritten entry's latest provenance
   * is the import (E12/DS3/DS10).
   */
  async update(
    id: string,
    input: TranslationMemoryEntryInput,
    personId: string | null,
    provenance: EntryProvenance = {},
  ): Promise<TranslationMemoryEntry | null> {
    try {
      await this.dClient.items.update(id, {
        collection: input.collectionId,
        source_term: input.sourceTerm,
        source_language: input.sourceLanguage.id,
        translation: input.translation,
        translation_language: input.translationLanguage?.id ?? null,
        comment: input.comment ?? '',
        last_updated_by_user: personId,
        ...(provenance.origin && { origin: provenance.origin }),
        ...(provenance.importId !== undefined && {
          import: provenance.importId,
        }),
      });
      await this.dClient.items.publish(id);
      return this.echo(id, input, provenance.origin);
    } catch (error) {
      this.logger.error(
        'Error updating translation memory entry',
        extractErrorInfo(error),
      );
      return null;
    }
  }

  /**
   * Bulk create/overwrite entries (import commit, PLAT-10848). Writes run in
   * bounded-concurrency batches of {@link WRITE_CONCURRENCY} as drafts (no
   * per-item publish), then every touched id is published via `items.bulkPublish`
   * in {@link PUBLISH_BATCH}-sized chunks — vs. the old per-row
   * `find`+`create`+`publish` serial loop (three round-trips each). The
   * item-type lookup is hoisted once. Failed individual writes are logged,
   * dropped (mirrors `create`/`update` returning null) and counted in `failed`
   * so the caller can report a partial commit; a failed publish batch is logged
   * but does not abort the rest.
   */
  async writeMany(
    showcaseId: string,
    writes: EntryBulkWrite[],
    personId: string | null,
    provenance: EntryProvenance = {},
  ): Promise<BulkWriteResult> {
    const itemTypeId = await this.entryItemTypeId();
    const origin = provenance.origin ?? 'manual';

    type Tagged = {
      kind: 'created' | 'overwritten';
      id: string;
      entry: TranslationMemoryEntry;
    };

    const writeOne = async (write: EntryBulkWrite): Promise<Tagged | null> => {
      const { input } = write;
      try {
        if (write.existingEntryId) {
          await this.dClient.items.update(write.existingEntryId, {
            collection: input.collectionId,
            source_term: input.sourceTerm,
            source_language: input.sourceLanguage.id,
            translation: input.translation,
            translation_language: input.translationLanguage?.id ?? null,
            comment: input.comment ?? '',
            last_updated_by_user: personId,
            ...(provenance.origin && { origin: provenance.origin }),
            ...(provenance.importId !== undefined && {
              import: provenance.importId,
            }),
          });
          return {
            kind: 'overwritten',
            id: write.existingEntryId,
            entry: this.echo(write.existingEntryId, input, provenance.origin),
          };
        }
        const created = await this.dClient.items.create({
          item_type: { type: 'item_type', id: itemTypeId },
          showcase: showcaseId,
          collection: input.collectionId,
          source_term: input.sourceTerm,
          source_language: input.sourceLanguage.id,
          translation: input.translation,
          translation_language: input.translationLanguage?.id ?? null,
          comment: input.comment ?? '',
          origin,
          import: provenance.importId ?? null,
          created_by_user: personId,
          last_updated_by_user: personId,
        });
        return {
          kind: 'created',
          id: created.id,
          entry: this.echo(created.id, input, origin),
        };
      } catch (error) {
        this.logger.error(
          'Error writing translation memory entry (bulk)',
          extractErrorInfo(error),
        );
        return null;
      }
    };

    const results: (Tagged | null)[] = [];
    for (const batch of chunkArray(
      writes,
      TranslationMemoryEntryRepository.WRITE_CONCURRENCY,
    )) {
      results.push(...(await Promise.all(batch.map(writeOne))));
    }
    const succeeded = results.filter((r): r is Tagged => r !== null);

    for (const batch of chunkArray(
      succeeded.map(r => r.id),
      TranslationMemoryEntryRepository.PUBLISH_BATCH,
    )) {
      try {
        await this.dClient.items.bulkPublish({
          items: batch.map(id => ({ type: 'item', id })),
        });
      } catch (error) {
        this.logger.error(
          'Error bulk-publishing translation memory entries',
          extractErrorInfo(error),
        );
      }
    }

    return {
      created: succeeded.filter(r => r.kind === 'created').map(r => r.entry),
      overwritten: succeeded
        .filter(r => r.kind === 'overwritten')
        .map(r => r.entry),
      failed: writes.length - succeeded.length,
    };
  }

  /** Hard-delete an entry (no `is_deleted` field on the model). */
  async delete(id: string): Promise<boolean> {
    try {
      await this.dClient.items.destroy(id);
      return true;
    } catch (error) {
      this.logger.error(
        'Error deleting translation memory entry',
        extractErrorInfo(error),
      );
      return false;
    }
  }

  /**
   * Build the confirmed entry from the write input so the store can apply it in
   * place without refetching (GraphQL CDN lag). Timestamps are stamped with the
   * write time so the row shows "Updated" immediately (the "Updated" column read
   * `updatedAt ?? createdAt`, which was blank right after a write); the next load
   * reconciles them with the authoritative Dato meta. `origin` is preserved on
   * update.
   */
  private echo(
    id: string,
    input: TranslationMemoryEntryInput,
    origin?: string,
  ): TranslationMemoryEntry {
    const now = new Date().toISOString();
    return {
      id,
      sourceTerm: input.sourceTerm,
      translation: input.translation,
      comment: input.comment ?? '',
      origin: origin ?? 'manual',
      // `origin` is only passed on create — stamp createdAt there; on update
      // leave it null (the display falls back to updatedAt).
      createdAt: origin ? now : null,
      updatedAt: now,
      collectionId: input.collectionId,
      sourceLanguage: input.sourceLanguage,
      translationLanguage: input.translationLanguage,
    };
  }
}
